import { composeAgents, init } from "@dafiti/observability-sdk";
import type { Instrumentation } from "@dafiti/observability-sdk";
import { DatadogAdapter } from "@dafiti/observability-sdk/datadog";
import { InstanaAdapter } from "@dafiti/observability-sdk/instana";
import { env } from "next-runtime-env";

import packageJson from "../package.json";

/**
 * Errors in this deny list are intentionally ignored because they:
 * - are not actionable by the application
 * - do not represent real user-facing bugs
 * - cannot be fixed from application code
 * - would otherwise create alert noise and inflate RUM costs
 */
const ERROR_DENYLIST = {
  /**
   * Errors filtered by message content
   */
  messageIncludes: [
    /**
     * Triggered by browser layout observation edge cases.
     * Known Chromium/WebKit issue.
     * Does not impact user interaction or application state.
     */
    "ResizeObserver loop limit exceeded",

    /**
     * Same ResizeObserver issue with a different message variant.
     * Safe to ignore.
     */
    "ResizeObserver loop completed with undelivered notifications",

    /**
     * Cross-origin script errors where the browser blocks stack traces.
     * Commonly caused by browser extensions or third-party scripts.
     * Not debuggable from our codebase.
     */
    "Script error.",

    /**
     * Security error thrown when an iframe is blocked due to
     * cross-origin or sandbox restrictions.
     * Expected behavior enforced by the browser.
     */
    "Blocked a frame with origin",

    /**
     * Browser security restriction (usually in private mode,
     * older Safari versions, or hardened browser configs).
     * Not a regression in application logic.
     */
    "The operation is insecure",

    /**
     * Thrown when the browser blocks an action that requires
     * explicit user permission (autoplay, clipboard, camera, etc).
     * This is expected and handled by the UI.
     */
    "NotAllowedError",

    /**
     * Generic network failure when fetch/XHR is interrupted
     * by the browser, extensions, connectivity loss, or CORS.
     * No server request is actually processed.
     */
    "NetworkError when attempting to fetch resource",

    /**
     * Resource loading failure without HTTP status.
     * Common on flaky connections or blocked third-party assets.
     */
    "Load failed",

    /**
     * Request was intentionally aborted by the browser or application
     * (navigation, route change, AbortController).
     * Expected control flow, not an error.
     */
    "The user aborted a request",

    /**
     * Same aborted-request scenario with alternate wording.
     */
    "Request aborted",

    /**
     * Internal WebKit error seen on iOS/Safari under memory pressure.
     * Not reproducible and not actionable.
     */
    "WebKit encountered an internal error",

    /**
     * Browser intervention warning.
     * Emitted when preventDefault() is called on a non-cancelable event
     * during active scrolling or passive listeners.
     * Not actionable and does not affect user experience.
     */
    "intervention: Ignored attempt to cancel a",

    /**
     * Happens when Trustvox fails to load get-consent endpoint
     * Does not affect user experience.
     *
     * @ref https://app.datadoghq.com/error-tracking?query=service%3Aweb-frontend-checkout-xp&et-side=data&et-viz=events&fromUser=false&issue_states=open&order=total_count&refresh_mode=sliding&source=all&sp=%5B%7B%22p%22%3A%7B%22issueId%22%3A%2242612814-b474-11f0-85d5-da7ad0900002%22%7D%2C%22i%22%3A%22error-tracking-issue%22%7D%2C%7B%22p%22%3A%7B%22eventId%22%3A%22AwAAAZu8jYBQpaAxlgAAABhBWnU4allPbkFBREJScnBibGJOalNPbXgAAAAkMTE5YmJjYjctY2M3Ni00NjlkLThhYTctMzZkMDY4ZDAyMWVkAAO_TA%22%7D%2C%22i%22%3A%22error-tracking-error-event%22%7D%5D&from_ts=1767792944343&to_ts=1768397744343&live=true
     */
    "Cannot read properties of undefined (reading 'config')",

    /**
     * Sometimes fails when loading Google Tag Manager
     *
     * @ref https://app.datadoghq.com/error-tracking?query=service%3Aweb-frontend-checkout-xp&et-side=data&et-viz=events&fromUser=false&issue_states=open&order=total_count&refresh_mode=sliding&source=all&sp=%5B%7B%22p%22%3A%7B%22issueId%22%3A%2242612814-b474-11f0-85d5-da7ad0900002%22%7D%2C%22i%22%3A%22error-tracking-issue%22%7D%2C%7B%22p%22%3A%7B%22eventId%22%3A%22AwAAAZu8XqeNgItgDQAAABhBWnU4WHFlUkFBQ1BUcnd4OHM2NUhzc2gAAAAkMDE5YmJjNWYtMTZhYy00YWMwLTlkMjgtOTZkMmExYmJmNTgwAACAMQ%22%7D%2C%22i%22%3A%22error-tracking-error-event%22%7D%5D&from_ts=1767792944343&to_ts=1768397744343&live=true
     */
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')",

    /**
    /**
     * An unknown error occurred while enabling keyboard logging.
     * Likely caused by a missing or unavailable Java object during Android instrumentation.
     * Not actionable or indicative of a user-impacting problem.
     */
    "Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone",
  ],

  /**
   * Errors filtered by stack trace content
   */
  stackIncludes: [
    /**
     * Errors originating from Chrome browser extensions.
     * Outside application control.
     */
    "chrome-extension://",

    /**
     * Errors originating from Firefox browser extensions.
     * Outside application control.
     */
    "moz-extension://",

    /**
     * Safari browser extensions injecting scripts.
     * Not related to application code.
     */
    "safari-extension://",

    /**
     * Generic marker used by injected or obfuscated third-party scripts
     * (often ad blockers or privacy tools).
     */
    "extensions::",

    /**
     * Error that happens when Trustvox fails to load review plugin
     */
    "sincero/sincero.js",

    /**
     * Errors from stape controlled by GTM
     */
    "dtag/v8.js",
  ],
};

type FetchResourceEvent = {
  type: "resource";
  resource?: {
    type?: string;
  };
  context?: Record<string, unknown>;
};

type FetchResponseContext = {
  response?: {
    headers?: Iterable<[string, string]>;
  };
};

const BOT_OR_CRAWLER_USER_AGENT_REGEX =
  /(bot|crawler|spider|crawling|googlebot|bingbot|yandex|duckduckbot|baiduspider|semrush|ahrefs|facebookexternalhit|slurp|applebot|headlesschrome|lighthouse|pagespeed)/i;

const isBotOrCrawlerUserAgent = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) return false;

  return BOT_OR_CRAWLER_USER_AGENT_REGEX.test(navigator.userAgent);
};

/**
 * Creates a new instrumentation instance
 */
export const createInstrumentation = () => {
  const providers: Array<Instrumentation> = [];

  const NEXT_PUBLIC_INSTANA_ENABLED = env("NEXT_PUBLIC_INSTANA_ENABLED");

  const NEXT_PUBLIC_INSTANA_KEY = env("NEXT_PUBLIC_INSTANA_KEY");

  const NEXT_PUBLIC_INSTANA_INTEGRITY = env("NEXT_PUBLIC_INSTANA_INTEGRITY");

  const NEXT_PUBLIC_DATADOG_ENABLED = env("NEXT_PUBLIC_DATADOG_ENABLED");

  const NEXT_PUBLIC_DATADOG_APPLICATION_ID = env(
    "NEXT_PUBLIC_DATADOG_APPLICATION_ID",
  );

  const NEXT_PUBLIC_DATADOG_CLIENT_TOKEN = env(
    "NEXT_PUBLIC_DATADOG_CLIENT_TOKEN",
  );

  const NEXT_PUBLIC_ENVIRONMENT = env("NEXT_PUBLIC_ENVIRONMENT");

  const NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE = env(
    "NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE",
  );

  const NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE = env(
    "NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE",
  );

  const NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL = env(
    "NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL",
  );

  const isInstanaEnabled =
    NEXT_PUBLIC_INSTANA_ENABLED === "enabled" &&
    process.env.NEXT_RUNTIME !== "edge"; // instana is not supported on edge runtime

  if (isInstanaEnabled) {
    const instana = init(InstanaAdapter, {
      key: NEXT_PUBLIC_INSTANA_KEY || "",
      integrity: NEXT_PUBLIC_INSTANA_INTEGRITY || "",
    });

    providers.push(instana);
  }

  const isClientSide = globalThis.window !== undefined;
  const isClientBotOrCrawler = isClientSide && isBotOrCrawlerUserAgent();

  // enable datadog only if is client side or if is enabled on server side
  const isDatadogEnabled =
    !isClientBotOrCrawler &&
    (NEXT_PUBLIC_DATADOG_ENABLED === "enabled" ||
      (NEXT_PUBLIC_DATADOG_ENABLED === "client-only" && isClientSide));

  if (isDatadogEnabled) {
    const datadog = init(DatadogAdapter, {
      applicationId: NEXT_PUBLIC_DATADOG_APPLICATION_ID || "",
      clientToken: NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || "",
      version: packageJson.version,
      env: NEXT_PUBLIC_ENVIRONMENT,
      sessionSampleRate: Number(
        NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE || "20",
      ),
      sessionReplaySampleRate: Number(
        NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE || "5",
      ),
      defaultPrivacyLevel: (NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL ||
        "mask-user-input") as "allow" | "mask" | "mask-user-input",
      service: process.env.DD_SERVICE || "web-frontend-checkout-xp",
      beforeSend: (event, context) => {
        const isFetchResourceEvent =
          event.type === "resource" &&
          (event as FetchResourceEvent).resource?.type === "fetch";

        const responseHeaders = (context as FetchResponseContext).response
          ?.headers;

        if (isFetchResourceEvent && responseHeaders) {
          try {
            const eventContext = (event as FetchResourceEvent).context ?? {};
            eventContext.responseHeaders = Object.fromEntries(responseHeaders);
            (event as FetchResourceEvent).context = eventContext;
          } catch {}
        }

        if (event.type !== "error") return true;

        const error = event.error;

        if (!error) return true;

        if (
          typeof error.message === "string" &&
          ERROR_DENYLIST.messageIncludes.some((message) =>
            error.message?.includes(message),
          )
        ) {
          return false;
        }

        if (
          typeof error.stack === "string" &&
          ERROR_DENYLIST.stackIncludes.some((stack) =>
            error.stack?.includes(stack),
          )
        ) {
          return false;
        }

        return true;
      },
    });

    providers.push(datadog);
  }

  return composeAgents(...providers);
};

/**
 * The instrumentation instance that can be used to track events and errors on node environments
 * To use it on client side, please use the `useInstrumentation` hook
 */
export const instrumentation = createInstrumentation();
